class PlatformDetector {
  
  // Get User Agent
  getUserAgent() {
    return navigator.userAgent || navigator.vendor || window.opera || '';
  }

  // Check if running in Android WebView
  isAndroidWebView() {
    const ua = this.getUserAgent().toLowerCase();
    return (
      ua.includes('android') && 
      (ua.includes('wv') || // Android WebView indicator
       ua.includes('webview') ||
       (ua.includes('android') && !ua.includes('mobile safari')))
    );
  }

  // Check if running in iOS WebView
  isIOSWebView() {
    const ua = this.getUserAgent().toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    
    // iOS WebView doesn't have 'Safari' in user agent
    // But has 'AppleWebKit'
    const hasSafari = ua.includes('safari');
    const hasAppleWebKit = ua.includes('applewebkit');
    
    return isIOS && hasAppleWebKit && !hasSafari;
  }

  // Check if running in any WebView
  isWebView() {
    // Check for React Native WebView
    if (window.ReactNativeWebView) {
      return true;
    }
    
    // Check for Android or iOS WebView
    return this.isAndroidWebView() || this.isIOSWebView();
  }

  // Check if Chrome on Android
  isChromeAndroid() {
    const ua = this.getUserAgent().toLowerCase();
    return (
      ua.includes('android') && 
      ua.includes('chrome') &&
      !ua.includes('edg') &&
      !ua.includes('opr') &&
      !this.isAndroidWebView()
    );
  }

  // Check if Safari on iOS
  isSafariIOS() {
    const ua = this.getUserAgent().toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const hasSafari = ua.includes('safari');
    const hasChrome = ua.includes('chrome') || ua.includes('crios');
    
    return isIOS && hasSafari && !hasChrome;
  }

  // Check if Chrome on iOS
  isChromeIOS() {
    const ua = this.getUserAgent().toLowerCase();
    return (
      /iphone|ipad|ipod/.test(ua) && 
      ua.includes('crios')
    );
  }

  // Check if Firefox
  isFirefox() {
    const ua = this.getUserAgent().toLowerCase();
    return ua.includes('firefox') || ua.includes('fxios');
  }

  // Check if Edge
  isEdge() {
    const ua = this.getUserAgent().toLowerCase();
    return ua.includes('edg/');
  }

  // Check if Opera
  isOpera() {
    const ua = this.getUserAgent().toLowerCase();
    return ua.includes('opr/') || ua.includes('opera');
  }

  // Check if Samsung Internet
  isSamsungInternet() {
    const ua = this.getUserAgent().toLowerCase();
    return ua.includes('samsungbrowser');
  }

  // Check if mobile device
  isMobile() {
    const ua = this.getUserAgent().toLowerCase();
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
  }

  // Check if tablet
  isTablet() {
    const ua = this.getUserAgent().toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(ua);
  }

  // Check if desktop
  isDesktop() {
    return !this.isMobile() && !this.isTablet();
  }

  // Check if iOS
  isIOS() {
    const ua = this.getUserAgent().toLowerCase();
    return /iphone|ipad|ipod/.test(ua);
  }

  // Check if Android
  isAndroid() {
    const ua = this.getUserAgent().toLowerCase();
    return ua.includes('android');
  }

  // Check if Windows
  isWindows() {
    const ua = this.getUserAgent().toLowerCase();
    return ua.includes('windows');
  }

  // Check if Mac
  isMac() {
    const ua = this.getUserAgent().toLowerCase();
    return ua.includes('mac') && !this.isIOS();
  }

  // Check if Linux
  isLinux() {
    const ua = this.getUserAgent().toLowerCase();
    return ua.includes('linux') && !ua.includes('android');
  }

  // Get OS name
  getOS() {
    if (this.isIOS()) return 'iOS';
    if (this.isAndroid()) return 'Android';
    if (this.isWindows()) return 'Windows';
    if (this.isMac()) return 'macOS';
    if (this.isLinux()) return 'Linux';
    return 'Unknown';
  }

  // Get browser name
  getBrowser() {
    if (this.isAndroidWebView()) return 'Android WebView';
    if (this.isIOSWebView()) return 'iOS WebView';
    if (this.isChromeAndroid()) return 'Chrome Android';
    if (this.isChromeIOS()) return 'Chrome iOS';
    if (this.isSafariIOS()) return 'Safari iOS';
    if (this.isEdge()) return 'Edge';
    if (this.isFirefox()) return 'Firefox';
    if (this.isOpera()) return 'Opera';
    if (this.isSamsungInternet()) return 'Samsung Internet';
    
    const ua = this.getUserAgent().toLowerCase();
    if (ua.includes('chrome')) return 'Chrome';
    if (ua.includes('safari')) return 'Safari';
    
    return 'Unknown Browser';
  }

  // Get device type
  getDeviceType() {
    if (this.isTablet()) return 'Tablet';
    if (this.isMobile()) return 'Mobile';
    if (this.isDesktop()) return 'Desktop';
    return 'Unknown';
  }

  // Check if touch device
  isTouchDevice() {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );
  }

  // Check if PWA
  isPWA() {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  }

  // Get screen size category
  getScreenSize() {
    const width = window.innerWidth;
    
    if (width < 576) return 'xs'; // Extra small (mobile)
    if (width < 768) return 'sm'; // Small (mobile landscape)
    if (width < 992) return 'md'; // Medium (tablet)
    if (width < 1200) return 'lg'; // Large (desktop)
    return 'xl'; // Extra large (large desktop)
  }

  // Get complete platform info
  getPlatformInfo() {
    return {
      // Basic info
      userAgent: this.getUserAgent(),
      browser: this.getBrowser(),
      os: this.getOS(),
      deviceType: this.getDeviceType(),
      
      // Environment
      isWebView: this.isWebView(),
      isAndroidWebView: this.isAndroidWebView(),
      isIOSWebView: this.isIOSWebView(),
      isPWA: this.isPWA(),
      
      // Browser specific
      isChromeAndroid: this.isChromeAndroid(),
      isChromeIOS: this.isChromeIOS(),
      isSafariIOS: this.isSafariIOS(),
      isFirefox: this.isFirefox(),
      isEdge: this.isEdge(),
      isOpera: this.isOpera(),
      isSamsungInternet: this.isSamsungInternet(),
      
      // Device
      isMobile: this.isMobile(),
      isTablet: this.isTablet(),
      isDesktop: this.isDesktop(),
      isTouchDevice: this.isTouchDevice(),
      
      // OS specific
      isIOS: this.isIOS(),
      isAndroid: this.isAndroid(),
      isWindows: this.isWindows(),
      isMac: this.isMac(),
      isLinux: this.isLinux(),
      
      // Screen
      screenSize: this.getScreenSize(),
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      pixelRatio: window.devicePixelRatio,
      orientation: window.screen.orientation?.type || 'unknown',
      
      // Network
      isOnline: navigator.onLine,
      connectionType: navigator.connection?.effectiveType || 'unknown',
      
      // Features
      cookiesEnabled: navigator.cookieEnabled,
      hasServiceWorker: 'serviceWorker' in navigator,
      hasGeolocation: 'geolocation' in navigator,
      
      // Language & Region
      language: navigator.language,
      languages: navigator.languages,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  // Get simple platform string
  getPlatformString() {
    const browser = this.getBrowser();
    const os = this.getOS();
    const device = this.getDeviceType();
    
    return `${browser} on ${os} (${device})`;
  }
}

export default new PlatformDetector();